"use strict";

/**
 * Cart Modal (safe refresh)
 * Fix for stores where "/?section_id=s-cart-modal" returns 500.
 *
 * Strategy:
 * 1) Prefer "/cart?section_id=<handle>" (usually stable)
 * 2) Fallback to "/?section_id=<handle>"
 * 3) Fallback to "/?sections=<handle>" (JSON endpoint)
 */
(function () {
  class SCartModal extends HTMLElement {
    constructor() {
      super();

      this.sectionHandle = null;

      this.body = null;
      this.overlay = null;
      this.headerItemCount = null;
      this.btnCloser = null;

      this.recommendationAccordion = null;
      this.recommendationToggle = null;
      this.recommendationPanel = null;
      this.onRecommendationToggle = null;

      this.updateCart = this.updateCart.bind(this);
      this.openModal = this.openModal.bind(this);
      this.closeModal = this.closeModal.bind(this);
      this._onOverlayClick = this._onOverlayClick.bind(this);
    }

    connectedCallback() {
      this.sectionHandle = this.dataset.sectionId || "s-cart-modal";

      this.body = document.body;
      this.overlay = document.querySelector(".js-overlay");
      this.headerItemCount = document.querySelector(".cart-link__bubble-num");

      this.bindCloser();
      this.bindRecommendationAccordion();

      if (window.PubSub?.subscribe) {
        window.PubSub.subscribe("cart-modal:open", this.openModal);
        window.PubSub.subscribe("cart:update", this.updateCart);
      }

      document.addEventListener("DOMContentLoaded", () => {
        this.style.display = "";
      });
    }

    disconnectedCallback() {
      this.unbindCloser();
      this.unbindOverlay();
      this.unbindRecommendationAccordion();
    }

    bindCloser() {
      this.btnCloser = this.querySelector(".js-cart-modal__closer");
      if (!this.btnCloser) return;

      this.btnCloser.removeEventListener("click", this.closeModal);
      this.btnCloser.addEventListener("click", this.closeModal);
    }

    unbindCloser() {
      if (!this.btnCloser) return;
      this.btnCloser.removeEventListener("click", this.closeModal);
    }

    bindOverlay() {
      if (!this.overlay) return;

      this.overlay.removeEventListener("click", this._onOverlayClick);
      this.overlay.addEventListener("click", this._onOverlayClick);
    }

    unbindOverlay() {
      if (!this.overlay) return;
      this.overlay.removeEventListener("click", this._onOverlayClick);
    }

    bindRecommendationAccordion() {
      this.recommendationAccordion = this.querySelector("[data-cart-recommendation-accordion]");
      this.recommendationToggle = this.querySelector("[data-cart-recommendation-toggle]");
      this.recommendationPanel = this.querySelector("[data-cart-recommendation-panel]");

      if (!this.recommendationAccordion || !this.recommendationToggle || !this.recommendationPanel) return;

      this.onRecommendationToggle = this.onRecommendationToggle || this.toggleRecommendation.bind(this);

      this.recommendationToggle.removeEventListener("click", this.onRecommendationToggle);
      this.recommendationToggle.addEventListener("click", this.onRecommendationToggle);
    }

    unbindRecommendationAccordion() {
      if (!this.recommendationToggle || !this.onRecommendationToggle) return;
      this.recommendationToggle.removeEventListener("click", this.onRecommendationToggle);
    }

    toggleRecommendation() {
      if (!this.recommendationAccordion || !this.recommendationToggle || !this.recommendationPanel) return;

      const isOpen = this.recommendationAccordion.classList.contains("is-open");

      this.recommendationAccordion.classList.toggle("is-open", !isOpen);
      this.recommendationToggle.setAttribute("aria-expanded", String(!isOpen));
      this.recommendationPanel.hidden = isOpen;
    }

    _onOverlayClick() {
      this.closeModal();
    }

    openModal() {
      this.body?.classList.add("modal-open");
      this.overlay?.classList.add("is-open");
      this.classList.add("is-open");

      this.bindOverlay();
      this.updateCart();
    }

    closeModal() {
      this.body?.classList.remove("modal-open");
      this.overlay?.classList.remove("is-open");
      this.classList.remove("is-open");

      this.unbindOverlay();
    }

    async fetchSectionHTML() {
      const root = window.Shopify?.routes?.root || "/";
      const handle = encodeURIComponent(this.sectionHandle);

      const candidates = [
        `${root}cart?section_id=${handle}`,
        `${root}?section_id=${handle}`
      ];

      for (const url of candidates) {
        try {
          const response = await fetch(url, {
            credentials: "same-origin",
            cache: "no-store"
          });

          if (!response.ok) continue;

          const html = await response.text();

          if (html && html.includes("s-cart-modal")) {
            return html;
          }
        } catch (error) {
          console.warn("[s-cart-modal] section fetch failed:", url, error);
        }
      }

      try {
        const response = await fetch(`${root}?sections=${handle}`, {
          credentials: "same-origin",
          cache: "no-store"
        });

        if (response.ok) {
          const data = await response.json();
          const html = data?.[this.sectionHandle];

          if (html && html.includes("s-cart-modal")) {
            return html;
          }
        }
      } catch (error) {
        console.warn("[s-cart-modal] JSON section fetch failed:", error);
      }

      return null;
    }

    async updateCart() {
      this.classList.add("is-loading");

      const currentBody = this.querySelector(".js-cart-modal__body");
      const previousScrollTop = currentBody ? currentBody.scrollTop : 0;

      try {
        const html = await this.fetchSectionHTML();

        if (!html) {
          console.error("[s-cart-modal] Could not fetch section HTML.");
          return;
        }

        const temp = document.createElement("div");
        temp.innerHTML = html;

        const freshCart = temp.querySelector(".s-cart-modal");

        if (!freshCart) {
          console.error("[s-cart-modal] Section HTML did not include .s-cart-modal.");
          return;
        }

        this.innerHTML = freshCart.innerHTML;

        const countEl = this.querySelector(".js-cart-modal__count");

        if (countEl && this.headerItemCount) {
          this.headerItemCount.innerHTML = countEl.dataset.cartCount || "";
        }

        this.bindCloser();
        this.bindRecommendationAccordion();

        const newBody = this.querySelector(".js-cart-modal__body");

        if (newBody) {
          newBody.scrollTop = previousScrollTop;
        }

        document.dispatchEvent(
          new CustomEvent("cart-modal:updated", {
            detail: {
              cartModal: this
            }
          })
        );
      } catch (error) {
        console.error("[s-cart-modal] updateCart failed:", error);
      } finally {
        this.classList.remove("is-loading");
      }
    }
  }

  if (!customElements.get("s-cart-modal")) {
    customElements.define("s-cart-modal", SCartModal);
  }
})();
